import styles from "./Hero.module.scss";
import { Header_Startseite, ImageType } from "@/wp/types/acf-header-types";
import { Section } from "@/components/Section/Section";
import { HeroCanvas } from "./HeroCanvas";
import { PromoBox } from "./PromoBox/PromoBox";
import useWindowSize from "@/hooks/useWindowSize";
import { getImageType } from "@/utils/responsiveFile";
import { Button } from "@/components/Button/Button";
import { ExtendedLink } from "@/components/ExtededLink/ExtendedLink";
import { Headline } from "@/components/Headline/Headline";
import { useSublinesVisibility } from "@/hooks/useSublinesOverlinesVisibility";
import { passACFLink } from "@/utils/passACFLink";
import { StartPageType } from "@/types/ModuleOptionsType";
import { CustomSVG } from "@/components/_primitives/CustomSVG/CustomSVG";
import { LogosType } from "@/types/OptionType";
import { StaggerAnimation } from "@/components/Animation/StaggerAnimation";

interface HeroProps {
  header: Header_Startseite["header_startseite"];
  startPageOptions: StartPageType;
  logos?: LogosType[];
  isNobleElements?: boolean;
}

export const Hero = ({
  header,
  startPageOptions,
  logos,
  isNobleElements = false,
}: HeroProps) => {
  const { media, promobox } = header;
  const { image_desktop, image_tablet, image_mobile, image_fullhd } = media;

  const { width, height } = useWindowSize();
  const isShortWidescreen = width >= 1216 && height > 0 && height < 470;

  // Get the first logo from props
  const firstLogo = isNobleElements
    ? logos?.[1]?.logo_negative
    : logos?.[0]?.logo_positive;
  const logoSrc = firstLogo ? firstLogo.url : undefined;
  const logoAlt = firstLogo ? firstLogo.alt || "Logo" : "Logo";

  const {
    button_type,
    show_overline_subline,
    second_button_type,
    two_buttons,
    show_extended_link,
  } = startPageOptions;

  const { showOverline, showSubline } = useSublinesVisibility(
    show_overline_subline ?? "none",
  );

  const activeImage = getImageType({
    width: width,
    images: {
      image_fullhd: image_fullhd
        ? image_fullhd.url
          ? image_fullhd
          : false
        : false,
      image_desktop: image_desktop as ImageType, // Assuming image_desktop is always provided and valid
      image_tablet: image_tablet
        ? image_tablet.url
          ? image_tablet
          : false
        : false,
      image_mobile: image_mobile
        ? image_mobile.url
          ? image_mobile
          : false
        : false,
    },
  });

  return (
    <Section
      tag="header"
      className={styles["startseite-header"]}
      disableContainer
      disableColumns
    >
      <div className="is-relative is-clipped">
        {/* Canvas-based masked image for responsive shape with fixed corner radii */}
        {activeImage && (
          <HeroCanvas
            imageSrc={activeImage.url}
            imageAlt={activeImage.alt || "Hero image"}
            isNobleElements={isNobleElements}
            hidePromoboxCutout={isShortWidescreen}
          />
        )}

        {/* Hero Content with Logo, Headline, Text, and Button */}
        <div className={styles["hero-content"]}>
          <div className="columns m-0">
            <div className="column is-7-desktop">
              <div className={styles["header-info-wrapper"]}>
                <StaggerAnimation animation="fade-in-up" staggerDelay={150}>
                  {logoSrc && (
                    <div className={styles["hero-logo"]}>
                      <CustomSVG
                        src={logoSrc}
                        alt={logoAlt}
                        aria-hidden={true}
                      />
                    </div>
                  )}

                  <div>
                    {header.title && (
                      <Headline
                        headline_html_tag={"h1-hero"}
                        headline={header.title}
                        overline={showOverline ? header.overline : ""}
                        subline={showSubline ? header.subline : ""}
                        disableAnimation
                      />
                    )}
                  </div>
                  <div className="content">
                    {header.teasertext.title && (
                      <span className="title">{header.teasertext.title}</span>
                    )}
                    {header.teasertext.content && (
                      <div
                        className="intro-text"
                        dangerouslySetInnerHTML={{
                          __html: header.teasertext.content,
                        }}
                      />
                    )}
                    <div className={styles["links-wrapper"]}>
                      {show_extended_link ? (
                        <>
                          {header.link_extended && (
                            <ExtendedLink
                              extendedLink={header.link_extended}
                              btnType={
                                button_type
                                  ? button_type
                                  : header.link_extended.type === "email"
                                    ? "secondary"
                                    : "primary"
                              }
                            />
                          )}
                          {two_buttons && header.link_2_extended && (
                            <ExtendedLink
                              extendedLink={header.link_2_extended}
                              btnType={
                                button_type
                                  ? button_type
                                  : header.link_2_extended.type === "email"
                                    ? "secondary"
                                    : "primary"
                              }
                            />
                          )}
                        </>
                      ) : (
                        <>
                          {header.link && header.link.url && (
                            <Button
                              type={button_type ?? "primary"}
                              {...passACFLink(header, "link")}
                            >
                              {header.link.title}
                            </Button>
                          )}
                          {two_buttons &&
                            header.link_2 &&
                            header.link_2.url && (
                              <Button
                                type={second_button_type ?? "secondary"}
                                {...passACFLink(header, "link_2")}
                              >
                                {header.link_2.title}
                              </Button>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                </StaggerAnimation>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PromoBox positioned at bottom-right */}
      <PromoBox promobox={promobox} />
    </Section>
  );
};
